import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuthSession } from "@/hooks/useAuthSession";
import AppPageMeta from "@/components/auth/AppPageMeta";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const OWNER_EMAIL = 'jstaack@staackllc.com';

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const location = useLocation();
  const { session, user, isReady, refreshSession } = useAuthSession();
  const activeUser = session?.user ?? user;
  const userId = activeUser?.id;
  const isOwner = activeUser?.email?.toLowerCase() === OWNER_EMAIL;
  const { isAdmin, isLoading: rolesLoading } = useUserRole(userId);

  // Separate effect for subscription/role checks - runs when userId or role state changes
  useEffect(() => {
    if (!isReady) return;

    let mounted = true;

    if (!userId) {
      const validateMissingSession = async () => {
        try {
          setIsCheckingAccess(true);
          const restoredSession = await refreshSession();
          if (!mounted) return;

          if (restoredSession?.user?.id) {
            return;
          }

          if (!restoredSession?.user?.id) {
            setHasAccess(false);
            setIsCheckingAccess(false);
          }
        } catch (error) {
          console.error('Error restoring auth session:', error);
          if (mounted) {
            setHasAccess(false);
            setIsCheckingAccess(false);
          }
        }
      };

      void validateMissingSession();
      return () => { mounted = false; };
    }

    if (isOwner) {
      setHasAccess(true);
      setIsCheckingAccess(false);
      return () => { mounted = false; };
    }

    if (rolesLoading) {
      setIsCheckingAccess(true);
      return;
    }

    const runChecks = async () => {
      try {
        setIsCheckingAccess(true);

        if (isAdmin) {
          if (mounted) { setHasAccess(true); setIsCheckingAccess(false); }
          return;
        }

        if (location.pathname === '/contacts' || location.pathname === '/calendar') {
          if (mounted) { setHasAccess(true); setIsCheckingAccess(false); }
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('subscription_status, payment_completed')
          .eq('id', userId)
          .maybeSingle();

        if (!mounted) return;

        if (profileError) {
          console.error('Error checking profile:', profileError);
          setHasAccess(true);
          setIsCheckingAccess(false);
          return;
        }

        if (profile?.payment_completed || !profile?.subscription_status || profile.subscription_status === 'active') {
          setHasAccess(true);
          setIsCheckingAccess(false);
          return;
        }

        setHasAccess(false);
        setIsCheckingAccess(false);
        navigate('/plans');
      } catch (e) {
        console.error('Error in auth checks', e);
        if (mounted) setIsCheckingAccess(false);
      }
    };

    runChecks();

    return () => { mounted = false; };
  }, [isReady, userId, isOwner, rolesLoading, isAdmin, location.pathname, navigate, refreshSession]);

  if (!isReady || isCheckingAccess) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!activeUser) {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from: currentRoute }} />;
  }

  return hasAccess ? <><AppPageMeta />{children}</> : null;
};

export default ProtectedRoute;
